import React from "react";
import { compact, join } from "lodash";
import styled from "styled-components";
import { FontWeight, message, transparentize } from "@glossy";
import { backgroundColors } from "@glossy/mixins";
import { dominant, form, onDominant } from "@glossy/theme";
import Icon, { Glyph } from "@ui-kit/icon";

interface IProps {
  text?: string | React.ReactNode;
  children?: React.ReactNode;
  icon?: Glyph;
  iconRight?: boolean;
  size?: Size;
  iconSize?: string | number;
  variant?: Variant;
  circular?: boolean;
  onClick?: (event: any) => void;
  isDisabled?: boolean;
  hasCaret?: boolean;
  isActive?: boolean;
  className?: string;
  type?: "button" | "submit" | "reset";
  fullWidth?: boolean;
}

export const SIZES = {
  xs: { height: 24, padding: 12, radius: 4, iconSize: 16 },
  s: { height: 32, padding: 12, radius: 4, iconSize: 20 },
  m: { height: 36, padding: 16, radius: 6, iconSize: 20 },
  l: { height: 48, padding: 16, radius: 8, iconSize: 20 },
};

export type Size = keyof typeof SIZES;

export const VARIANTS = {
  primary: { bg: dominant, text: onDominant },
  danger: { bg: (props) => message(props.theme, "error"), text: onDominant },
  secondary: { bg: () => "transparent", text: dominant },
  tertiary: { bg: () => "transparent", text: form },
};

export type Variant = keyof typeof VARIANTS;

const getVariantProp =
  (prop) =>
  ({ variant }) =>
    VARIANTS[variant][prop];

const getIconColor = (variant: Variant) => VARIANTS[variant]["text"]({});

const attr =
  (key: string) =>
  ({ size }: { size: Size }): string =>
    `${String(SIZES[size][key])}px`;
const height = attr("height");
const padding = attr("padding");
const radius = attr("radius");

const bgOverlay = (amount) => (props) =>
  backgroundColors([
    transparentize(amount, getVariantProp("text")(props)(props)),
    getVariantProp("bg")(props)(props),
  ]);

const Root = styled.button<{
  icon: Glyph;
  size: Size;
  iconOnly: boolean;
  variant: Variant;
  circular: boolean;
  isDisabled: boolean;
  fullWidth: boolean;
}>`
  vertical-align: middle;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: ${height};
  font-size: 14px;
  font-weight: ${FontWeight.Normal};
  width: ${(props) =>
    props.fullWidth ? "100%" : props.iconOnly && height(props)};
  padding: ${(props) => !props.iconOnly && `0 ${padding(props)}`};
  border-radius: ${(props) =>
    props.circular ? `${SIZES[props.size].height / 2}px` : radius};
  cursor: ${(props) => (props.isDisabled ? "default" : "pointer")};
  user-select: none;
  opacity: ${(props) => props.isDisabled && 0.5};
  background-color: ${getVariantProp("bg")};
  color: ${getVariantProp("text")};
  max-width: 100%;

  :hover {
    ${bgOverlay(0.85)};
  }

  :active,
  &.active {
    pointer-events: ${(props) => props.isDisabled && "none"};
  }

  :active,
  &.active,
  &[aria-expanded="true"] {
    ${bgOverlay(0.8)};
  }
`;

const Text = styled.span<{ withIcon: boolean; iconRight: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-wrap: normal;
  margin-left: ${({ withIcon, iconRight }) => withIcon && !iconRight && "12px"};
  margin-right: ${({ withIcon, iconRight }) => withIcon && iconRight && "12px"};
`;

const Caret = styled(Icon)`
  margin-left: 3px;
  margin-right: -3px;
  position: relative;
  top: 1px;
  display: inline-flex;
`;

/**
 * Use `button` to trigger an action on user click
 */
const UiKitButton = React.forwardRef(
  (
    {
      text,
      children,
      icon,
      iconRight = false,
      size = "s",
      iconSize,
      variant = "secondary",
      circular = false,
      onClick,
      isDisabled,
      hasCaret,
      isActive,
      className,
      type = "button",
      fullWidth = false,
      ...props
    }: IProps,
    ref: React.Ref<HTMLButtonElement>
  ) => (
    <Root
      iconOnly={!!icon && !text}
      size={size}
      fullWidth={fullWidth}
      onClick={onClick}
      icon={icon as Glyph}
      variant={variant}
      circular={circular}
      isDisabled={!!isDisabled}
      role="button"
      aria-disabled={!!isDisabled}
      ref={ref}
      className={join(compact([className, isActive && "active"]), " ")}
      {...props}
    >
      {!!icon && !iconRight && (
        <Icon
          color={getIconColor(variant)}
          size={iconSize || SIZES[size].iconSize}
          glyph={icon}
        />
      )}
      {!!text && (
        <Text iconRight={iconRight} withIcon={!!icon && !!text}>
          {text}
        </Text>
      )}
      {children}
      {!!icon && iconRight && (
        <Icon
          color={getIconColor(variant)}
          size={SIZES[size].iconSize}
          glyph={icon}
        />
      )}
      {!!hasCaret && (
        <Caret
          size="s"
          glyph="chevron-small-down"
          color={getIconColor(variant)}
        />
      )}
    </Root>
  )
);

export default UiKitButton;
